import { trackEvent } from '@root/utils/tracking';
import * as Sentry from '@sentry/nextjs';
import getConfig from 'next/config';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { loginWithGooglePopup, setToken } from 'slices/authSlice';

const { publicRuntimeConfig } = getConfig();

type GoogleOauthButtonTypes = 'signin_with' | 'signup_with' | 'continue_with';

// A shorter list of props is provided intentionally since we want this button
// to remain pretty consistent across our pages.
export interface GoogleOauthButtonProps {
  buttonType?: GoogleOauthButtonTypes;
  redirectToDesktop?: boolean;
  backToEmbed?: boolean;
  buttonSize?: 'small' | 'medium' | 'large'; // The size of the button. The default is large.
  buttonWidth?: string; // The minimum button width, in pixels. The maximum width is 400 px.
}

const handleResponse = async (response, dispatch) => {
  // @ts-expect-error - Should be solved when migrated to React-Query
  const userInfo = await dispatch(loginWithGooglePopup({ credential: response.credential }));

  if (userInfo && userInfo.payload && userInfo.payload.token) {
    userInfo.token = userInfo.payload.token;
    // @ts-expect-error - Should be solved when migrated to React-Query
    dispatch(setToken({ token: userInfo.payload.token, refresh: userInfo.payload.refresh }));
  }
};

const GoogleOauthButton = ({ buttonType, redirectToDesktop, backToEmbed, buttonSize = 'large', buttonWidth }: GoogleOauthButtonProps) => {
  const dispatch = useDispatch();

  useEffect(() => {
    if (backToEmbed) {
      // This is necessary for signins coming from embedded scribes. Avoids allow-scripts + allow-same-origin issues
      global?.window?.google?.accounts.id.initialize({
        client_id: publicRuntimeConfig.googleOauthClientId,
        ux_mode: 'popup',
        callback: (response) => handleResponse(response, dispatch)
      });
    } else {
      const redirectParamString = `${redirectToDesktop ? '?redirect_to_desktop=true' : ''}`;

      global?.window?.google?.accounts.id.initialize({
        client_id: publicRuntimeConfig.googleOauthClientId,
        ux_mode: 'redirect',
        login_uri: `${publicRuntimeConfig.scribeApiRootUrl}weblogin/social/jwt-pair/${redirectParamString}`
      });
    }
    try {
      global?.window?.google?.accounts?.id?.renderButton?.(document?.getElementById?.('googleOauthDiv'), {
        type: 'standard',
        theme: 'outline',
        size: buttonSize,
        text: buttonType,
        width: buttonWidth,
        click_listener: () => {
          trackEvent('clicked_google_sso_button', { button_type: buttonType });
        }
      });
    } catch (e) {
      console.error('Error rendering Google Oauth button', e);
      Sentry.captureException(e);
    }
  }, [backToEmbed]);
  return <div id='googleOauthDiv' data-testid='googleOauthDiv' />;
};

export default GoogleOauthButton;
